import raw from "./TEZAM-marketing-content.json";

export type FaqItem = { q: string; a: string };

export type HomeWhatYouGetCard = {
    title: string;
    description: string;
    bullets?: string[];
};

export type HomeHowStep = {
    title: string;
    text: string;
};

export type HomeIndustryItem = {
    label: string;
    oneLiner: string;
    hrefHint: string;
};

export type HomeClosing = {
    title?: string;
    text?: string;
    primaryCta?: string;
    primaryHref?: string;
    secondaryCta?: string;
    secondaryHref?: string;
};

export type HomeSection =
    | { id: "hero"; contentSource?: string }
    | { id: "whatYouGet"; title?: string; cards?: HomeWhatYouGetCard[] }
    | { id: "howItWorks"; title?: string; steps?: HomeHowStep[] }
    | { id: "eightIndustries"; title?: string; items?: HomeIndustryItem[] }
    | { id: "forWhomNot"; title?: string; items?: string[] }
    | ({ id: "closing" } & HomeClosing)
    | { id: string; title?: string; [key: string]: unknown };

export type MarketingHome = {
    seoTitle: string;
    seoDescription: string;
    h1: string;
    subhead: string;
    proofLine: string;
    primaryCta: string;
    secondaryCta: string;
    sections: HomeSection[];
    faq: FaqItem[];
};

export type MarketingPreset = {
    presetId: string;
    slug: string;
    label: string;
    seoTitle: string;
    seoDescription: string;
    h1: string;
    subhead: string;
    audience: string;
    clientJob: string;
    ownerJob: string;
    painPoints: string[];
    scenarioSteps: string[];
    modules: string[];
    nicheTeasers: Array<{ nicheId: string; label: string; teaser: string }>;
    faq: FaqItem[];
    primaryCta: string;
};

export type MarketingNiche = {
    nicheId: string;
    presetId: string;
    slug: string;
    url: string;
    label: string;
    seoTitle: string;
    seoDescription: string;
    h1: string;
    accentLine: string;
    subhead: string;
    forWhom: string;
    pains: string[];
    clientJourney: string[];
    ownerWins: string[];
    modulesEmphasized: string[];
    differentiatorsVsPreset: string;
    faq: FaqItem[];
    primaryCta: string;
};

type MarketingContent = {
    home: MarketingHome;
    presets: MarketingPreset[];
    niches: MarketingNiche[];
};

const content = raw as MarketingContent;

export function getHomeContent(): MarketingHome {
    return content.home;
}

export function getHomeSection<T extends HomeSection["id"]>(id: T): Extract<HomeSection, { id: T }> | undefined {
    return content.home.sections.find((s) => s.id === id) as Extract<HomeSection, { id: T }> | undefined;
}

export function getPresets(): MarketingPreset[] {
    return content.presets;
}

export function getPresetBySlug(slug: string): MarketingPreset | undefined {
    return content.presets.find((p) => p.slug === slug);
}

export function getNiches(): MarketingNiche[] {
    return content.niches;
}

export function getNicheByPath(presetSlug: string, nicheSlug: string): MarketingNiche | undefined {
    return content.niches.find((n) => n.slug === nicheSlug && n.url === `/solutions/${presetSlug}/${nicheSlug}`);
}

export function getNicheByNicheId(nicheId: string): MarketingNiche | undefined {
    return content.niches.find((n) => n.nicheId === nicheId);
}

export function getNichesByPresetId(presetId: string): MarketingNiche[] {
    return content.niches.filter((n) => n.presetId === presetId);
}

/** Legacy flat `/solutions/{slug}` niche pages (old offers). */
export function findNicheByLegacySlug(slug: string): MarketingNiche | undefined {
    const aliases: Record<string, string> = {
        restaurant: "horeca-restaurant",
        cafe: "horeca-cafe",
        "coffee-shop": "horeca-coffee",
        coffee: "horeca-coffee",
    };
    const nicheId = aliases[slug];
    if (nicheId) return getNicheByNicheId(nicheId);
    return content.niches.find((n) => n.slug === slug);
}

export function presetSlugFromId(presetId: string): string | undefined {
    return content.presets.find((p) => p.presetId === presetId)?.slug;
}

/** Split home h1 into core + tail around em/en dash. */
export function splitHomeH1(h1: string): { core: string; tail: string } {
    const match = h1.match(/^(.*?)\s*[—–-]\s*(.+)$/);
    if (match) return { core: match[1].trim(), tail: match[2].trim() };
    return { core: h1, tail: "" };
}
